// playoffs.js
// ---------------------------------------------------------------------------
// Playoff labeling for the board.
//
// THE KEY FACT: /matchups/<week> keeps working during playoff weeks. Sleeper
// populates real matchup_id pairings with live points for playoff games just
// like regular-season ones. So the row-building path in sleeperAdapter needs
// no changes at all -- the bracket is a LABEL LAYER on top of it, not a
// second data source.
//
// What the bracket buys us, in order of importance:
//   1. FILTERING. During playoff weeks /matchups returns rows for EVERY
//      roster, including non-playoff teams playing consolation games nobody
//      cares about. Without the bracket, week 15 shows six rows where two
//      matter. This is the strongest reason to fetch it.
//   2. LABELING. Round names (SEMIFINAL / CHAMPIONSHIP / 3RD PLACE).
//   3. ELIMINATION. `l` on a played entry tells us who is out.
//
// Everything here is pure. The only I/O is fetchBracket().
//
// Bracket shape (docs.sleeper.com#getting-the-playoff-bracket):
//   { r, m, t1, t2, w, l, t1_from?, t2_from?, p? }
//     r  round (1-indexed within the bracket)
//     m  match id, unique within the bracket
//     t1/t2  roster_id, or null when the slot is still "winner of match N"
//     w/l    roster_id of winner/loser once played
//     p      placement game marker: p:1 championship, p:3 third place, etc.
// ---------------------------------------------------------------------------

// Sleeper's default when a league object somehow lacks the setting. 15 is the
// modern default (14-week regular season); older leagues used 14.
export const DEFAULT_PLAYOFF_WEEK_START = 15;

// ---- basic questions ------------------------------------------------------

// The league's own setting is authoritative. Never guess week 14/15 from the
// NFL calendar -- leagues customize this constantly.
export function playoffWeekStart(league) {
  const s = Number(league?.settings?.playoff_week_start);
  return Number.isFinite(s) && s > 0 ? s : DEFAULT_PLAYOFF_WEEK_START;
}

export function isPlayoffWeek(week, league) {
  const w = Number(week);
  return Number.isFinite(w) && w >= playoffWeekStart(league);
}

// Which bracket round does a given NFL week correspond to? Round 1 is the
// first playoff week. Returns null outside the playoffs.
export function roundForWeek(week, league) {
  if (!isPlayoffWeek(week, league)) return null;
  return Number(week) - playoffWeekStart(league) + 1;
}

// Short form for the HEADER, where the budget is ~7 cells sitting next to
// the league name. `roundName` gives the full word for row labels; this
// gives the marquee form.
//
//   QUARTERFINAL -> Q-FINAL      SEMIFINAL -> SEMFNAL
//   CHAMPIONSHIP -> 'SHIP        ROUND 4   -> ROUND 4
//
// The apostrophe in 'SHIP is in SplitFlap's CHARSET (verified), so it
// renders as a real flap rather than a blank.
export function roundNameShort(round, totalRounds) {
  const remaining = totalRounds - round;
  if (remaining < 0 || !Number.isFinite(remaining)) return '';
  if (remaining === 0) return "'SHIP";
  if (remaining === 1) return 'SEMFNAL';
  if (remaining === 2) return 'Q-FINAL';
  return `ROUND ${round}`;
}

// Final placements, for the standings medals.
//
// Derived from the bracket rather than from regular-season rank, because
// the whole point of a playoff is that seeding doesn't decide it:
//   1st = winner of the p:1 game
//   2nd = LOSER of that same game
//   3rd = winner of the p:3 game (NOT the losing semifinalist -- if the
//         league plays a third-place game, that game decides it)
// Returns nulls when the games haven't been played, so the caller can
// simply not render a medal.
export function finalPlacements(bracket) {
  const out = { first: null, second: null, third: null };
  if (!Array.isArray(bracket) || !bracket.length) return out;

  const titled = bracket.find((e) => Number(e.p) === 1)
    || bracket.find((e) => Number(e.r) === maxRound(bracket) && e.w != null);
  if (titled && titled.w != null) {
    out.first = Number(titled.w);
    if (titled.l != null) out.second = Number(titled.l);
  }

  const bronze = bracket.find((e) => Number(e.p) === 3);
  if (bronze && bronze.w != null) out.third = Number(bronze.w);

  return out;
}

// Last place. Deliberately NOT taken from the losers bracket (which we
// don't fetch -- see handoff §13); this is simply the bottom of the
// regular-season standings, which is what most leagues mean by "last" and
// what the punishment usually attaches to.
//
// `standings` must already be rank-sorted ascending.
export function lastPlaceRosterId(standings) {
  if (!Array.isArray(standings) || !standings.length) return null;
  return standings[standings.length - 1].rosterId ?? null;
}

export function maxRound(bracket) {
  if (!Array.isArray(bracket) || !bracket.length) return 0;
  return bracket.reduce((mx, e) => Math.max(mx, Number(e.r) || 0), 0);
}

// ---- round naming ---------------------------------------------------------
//
// Named from the END of the bracket, not the start. A 4-team playoff's round
// 1 is a semifinal; a 6-team playoff's round 1 is a quarterfinal. Counting
// backwards from the final gets both right with no team-count special cases.
export function roundName(round, totalRounds) {
  const remaining = totalRounds - round;
  if (remaining < 0 || !Number.isFinite(remaining)) return '';
  if (remaining === 0) return 'CHAMPIONSHIP';
  if (remaining === 1) return 'SEMIFINAL';
  if (remaining === 2) return 'QUARTERFINAL';
  return `ROUND ${round}`;
}

// The label for ONE matchup, which is not always its round's label. Round 2
// of a 6-team bracket contains a semifinal AND a 5th-place game; calling both
// "SEMIFINAL" is a lie the board shouldn't tell.
//
// `p` is Sleeper's placement marker and beats the round name when present:
//   p:1 -> the title game        p:3 -> third place
//   p:5 -> fifth place           etc.
export function matchLabel(entry, totalRounds) {
  const p = Number(entry?.p);
  if (Number.isFinite(p) && p > 0) {
    if (p === 1) return 'CHAMPIONSHIP';
    if (p === 3) return '3RD PLACE';
    if (p === 5) return '5TH PLACE';
    if (p === 7) return '7TH PLACE';
    return `${p}TH PLACE`;
  }
  return roundName(Number(entry?.r), totalRounds);
}

// ---- joining the bracket to matchup rows ----------------------------------

// Index this round's bracket entries by the *pair* of roster_ids in them, so
// a matchup row can find its own bracket entry.
//
// We key on the sorted pair rather than on t1/t2 order: Sleeper's t1/t2 is
// seeding order, our teamA/teamB is whatever order /matchups returned. A
// sorted key makes the join order-independent.
//
// Entries whose slots are still unresolved (t1 or t2 null, i.e. "winner of
// match 3" before match 3 is played) can't be keyed by pair and are skipped.
// That's correct: if the slot is unresolved there is no matchup row for it
// yet either.
export function indexBracketByPair(bracket, round) {
  const map = new Map();
  if (!Array.isArray(bracket)) return map;
  for (const e of bracket) {
    if (Number(e.r) !== Number(round)) continue;
    const a = e.t1;
    const b = e.t2;
    if (a == null || b == null) continue;
    map.set(pairKey(a, b), e);
  }
  return map;
}

export function pairKey(a, b) {
  const x = Number(a);
  const y = Number(b);
  return x < y ? `${x}:${y}` : `${y}:${x}`;
}

// Every roster_id that appears anywhere in this round's bracket, resolved or
// not. Used to decide which /matchups rows are real playoff games.
export function rostersInRound(bracket, round) {
  const set = new Set();
  if (!Array.isArray(bracket)) return set;
  for (const e of bracket) {
    if (Number(e.r) !== Number(round)) continue;
    if (e.t1 != null) set.add(Number(e.t1));
    if (e.t2 != null) set.add(Number(e.t2));
  }
  return set;
}

// Rosters eliminated at ANY point up to and including `round`. A team is out
// once it appears as `l` on a played entry -- unless it's a consolation
// bracket where losers keep playing, which is why we only walk the winners
// bracket here.
export function eliminatedRosters(bracket, throughRound = Infinity) {
  const out = new Set();
  if (!Array.isArray(bracket)) return out;
  for (const e of bracket) {
    if (Number(e.r) > throughRound) continue;
    if (e.l != null) out.add(Number(e.l));
  }
  return out;
}

// Rosters that made the playoffs at all (appear anywhere in the bracket).
export function playoffRosters(bracket) {
  const set = new Set();
  if (!Array.isArray(bracket)) return set;
  for (const e of bracket) {
    if (e.t1 != null) set.add(Number(e.t1));
    if (e.t2 != null) set.add(Number(e.t2));
    if (e.w != null) set.add(Number(e.w));
    if (e.l != null) set.add(Number(e.l));
  }
  return set;
}

// A playoff team with no game this round has a BYE. We detect it as
// "in the bracket, not eliminated, but not playing this round".
export function byeRosters(bracket, round) {
  const playing = rostersInRound(bracket, round);
  const out = eliminatedRosters(bracket, round - 1);
  const all = playoffRosters(bracket);
  const byes = new Set();
  for (const id of all) {
    if (!playing.has(id) && !out.has(id)) byes.add(id);
  }
  return byes;
}

// ---- the championship week ------------------------------------------------
//
// WHY THIS EXISTS: resolveDisplayWeek walks back from the end of the season
// and stops at the first week with matchups. Off-season that can land on a
// consolation-only week -- a finished league's "FINAL" board showing the
// 7th-place game. The league's real final state is the week the title was
// decided.
//
// The championship entry is the one with p:1, or failing that any entry in
// the max round. Its week is playoff_week_start + r - 1.
export function championshipWeek(bracket, league) {
  if (!Array.isArray(bracket) || !bracket.length) return null;
  const titled = bracket.find((e) => Number(e.p) === 1);
  const round = titled ? Number(titled.r) : maxRound(bracket);
  if (!round) return null;
  return playoffWeekStart(league) + round - 1;
}

// Has the title actually been decided? Distinguishes "playoffs finished" from
// "playoffs scheduled but unplayed", which matters off-season: a league that
// never played its final should show its last PLAYED week, not an empty one.
export function championDecided(bracket) {
  if (!Array.isArray(bracket)) return false;
  const titled = bracket.find((e) => Number(e.p) === 1);
  if (titled) return titled.w != null;
  const mx = maxRound(bracket);
  return bracket.some((e) => Number(e.r) === mx && e.w != null);
}

export function championRosterId(bracket) {
  if (!Array.isArray(bracket)) return null;
  const titled = bracket.find((e) => Number(e.p) === 1);
  if (titled && titled.w != null) return Number(titled.w);
  const mx = maxRound(bracket);
  const last = bracket.find((e) => Number(e.r) === mx && e.w != null);
  return last ? Number(last.w) : null;
}

// ---- the one public transform the adapter calls ---------------------------
//
// Takes the rows buildBoard already produced plus the winners bracket, and
// returns { rows, playoff } where each surviving row has gained:
//   row.playoffLabel   'SEMIFINAL' | 'CHAMPIONSHIP' | '3RD PLACE' | ...
//   row.bracketMatchId the bracket's `m`, a STABLE id across the round
//   row.isPlayoff      true
//   row.isBye          true when one side sat out
//
// Rows for teams not in this round of the bracket are DROPPED by default.
// A departures board shows what is departing, not what already left.
// Pass { dropConsolation: false } to keep them dimmed instead.
export function annotatePlayoffRows(rows, bracket, week, league, opts = {}) {
  const { dropConsolation = true } = opts;
  const round = roundForWeek(week, league);

  if (!round || !Array.isArray(bracket) || !bracket.length) {
    return { rows, playoff: null };
  }

  const total = maxRound(bracket);
  const byPair = indexBracketByPair(bracket, round);
  const playing = rostersInRound(bracket, round);
  const byes = byeRosters(bracket, round);
  const out = [];

  for (const row of rows) {
    const a = row.teamA?.rosterId;
    const b = row.teamB?.rosterId;

    // Exact pair match: this row IS a bracket game.
    const entry = (a != null && b != null) ? byPair.get(pairKey(a, b)) : null;
    if (entry) {
      out.push({
        ...row,
        isPlayoff: true,
        isBye: false,
        bracketMatchId: Number(entry.m),
        playoffLabel: matchLabel(entry, total),
        // Rank is regular-season standing. During playoffs it reads as seed,
        // which is what people actually want to see.
        seedA: row.teamA?.rank ?? null,
        seedB: row.teamB?.rank ?? null,
      });
      continue;
    }

    // A bye: one side is a playoff team sitting this round out. Sleeper still
    // returns a matchup entry for them (sometimes paired with another bye
    // team, sometimes alone), so we surface it rather than dropping it. It's
    // the best-looking row on the board all season.
    const aBye = a != null && byes.has(a);
    const bBye = b != null && byes.has(b);
    if (aBye || bBye) {
      out.push({
        ...row,
        isPlayoff: true,
        isBye: true,
        bracketMatchId: null,
        playoffLabel: 'BYE',
        // Keep only the bye team on its own side; the "opponent" in a
        // bye-vs-bye pairing is not a real opponent.
        teamA: aBye ? row.teamA : null,
        teamB: bBye && !aBye ? row.teamB : (aBye ? null : row.teamB),
        seedA: row.teamA?.rank ?? null,
        seedB: row.teamB?.rank ?? null,
      });
      continue;
    }

    // Neither team is in the bracket this round -> consolation game.
    if (!dropConsolation) {
      out.push({
        ...row,
        isPlayoff: false,
        isBye: false,
        bracketMatchId: null,
        playoffLabel: 'CONSOLATN',
      });
    }
  }

  // Order playoff rows by importance, not by rank: the title game goes on
  // top. Placement number ascends (1 championship, 3 third, 5 fifth), byes
  // last, consolation after that. Within a tier, bracket match id keeps it
  // deterministic so nothing flips between polls.
  const weight = (r) => {
    if (r.isBye) return 900;
    if (!r.isPlayoff) return 950;
    if (r.playoffLabel === 'CHAMPIONSHIP') return 1;
    if (r.playoffLabel === 'SEMIFINAL') return 2;
    if (r.playoffLabel === 'QUARTERFINAL') return 3;
    const m = /^(\d+)/.exec(r.playoffLabel || '');
    return m ? 100 + Number(m[1]) : 500;
  };
  out.sort((x, y) =>
    (weight(x) - weight(y)) ||
    ((x.bracketMatchId ?? 9999) - (y.bracketMatchId ?? 9999)) ||
    (x.matchupId - y.matchupId));

  return {
    rows: out,
    playoff: {
      round,
      totalRounds: total,
      roundLabel: roundName(round, total),
      roundLabelShort: roundNameShort(round, total),
      playoffWeekStart: playoffWeekStart(league),
      eliminated: [...eliminatedRosters(bracket, round - 1)],
      inBracket: [...playoffRosters(bracket)],
      byes: [...byes],
      playing: [...playing],
      championRosterId: championDecided(bracket) ? championRosterId(bracket) : null,
    },
  };
}

// ---- I/O ------------------------------------------------------------------

// The only network call in this file. Returns [] on any failure -- a missing
// bracket must degrade to "no playoff labels", never to a broken board.
export async function fetchBracket(leagueId, fetchImpl = fetch, base = 'https://api.sleeper.app/v1') {
  try {
    const res = await fetchImpl(`${base}/league/${leagueId}/winners_bracket`);
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json) ? json : [];
  } catch {
    return [];
  }
}
